/**
 * Vue Router configuration
 *
 * 2025-12-25: Fixed Integrations route mapping - changed from /integrations to /dashboard/management
 */
import last from 'lodash/fp/last'
import Vue from 'vue'
import VueRouter from 'vue-router'
import { store } from './store'
Vue.use(VueRouter)

// Import critical components that should be loaded immediately
import Error from 'components/error'
import PublicLayout from 'components/layouts/public-layout'
import RootLayout from 'components/layouts/root-layout'
import Login from 'components/login'
import ResetPasswordPage from 'components/reset-password-page'
import { getV2BaseUrl } from '@/utils/v2-base-url'

// Remove all other component imports since we're using dynamic imports

/**
 * Get the v2 React route URL for a given module key if v2 is enabled
 * @param {string} moduleKey - The module key to check
 * @param {string} v2Route - The React app route path
 * @returns {string|null} - Full v2 URL or null if not v2
 */
const getV2RedirectUrl = (moduleKey, v2Route) => {
  const user = store.state.user
  if (!user) return null

  const moduleVersions = user.module_versions || {}
  const versionConfig = moduleVersions[moduleKey]

  if (versionConfig && versionConfig.module_version === 2) {
    return `${getV2BaseUrl()}${v2Route}`
  }
  return null
}

/**
 * Module route mappings for v2 redirects
 * Maps v1 routes to their corresponding module key and v2 route
 */
const V2_ROUTE_MAPPINGS = {
  '/dashboard/system': {
    moduleKey: 'dashboard',
    v2Route: '/app/dashboard/system',
  },
  // Dynamic route - matches /dashboard/reservation/:id
  '/dashboard/reservation/': {
    moduleKey: 'reservation_page',
    v2Route: '/app/reservation/',
    isDynamic: true,
  },
  '/dashboard/listings': {
    moduleKey: 'owners_listings',
    v2Route: '/app/listings',
  },
  '/dashboard/multi-calendar': {
    moduleKey: 'multi_calendar',
    v2Route: '/app/calendar',
  },
  '/dashboard/guest-experience': { moduleKey: 'crm', v2Route: '/app/crm' },
  '/dashboard/service-calls': {
    moduleKey: 'operations_field_ops',
    v2Route: '/app/dashboard/service-calls',
  },
  '/dashboard/taskim': {
    moduleKey: 'operations_office_tasks',
    v2Route: '/app/taskim/query',
  },
  '/dashboard/iot': {
    moduleKey: 'operations_iot',
    v2Route: '/app/iot-devices',
  },
  '/dashboard/iot/livion-all-keys': {
    moduleKey: 'operations_iot',
    v2Route: '/app/livion-all-keys',
  },
  '/dashboard/iot/livion-devices/': {
    moduleKey: 'operations_iot',
    v2Route: '/app/iot/livion-devices/',
    isDynamic: true,
  },
  '/dashboard/auto-tasks': {
    moduleKey: 'operations_automated_tasks',
    v2Route: '/app/auto-task-rules',
  },
  '/dashboard/guest-mgmt': {
    moduleKey: 'guests_experience',
    v2Route: '/app/experience/pre-check-ins',
  },
  '/dashboard/auto-messages': {
    moduleKey: 'guests_automated_messages',
    v2Route: '/app/guests/automated-messages',
  },
  '/dashboard/cm': {
    moduleKey: 'marketing_channels',
    v2Route: '/app/channels',
  },
  '/dashboard/reviews-dashboard': {
    moduleKey: 'marketing_reviews',
    v2Route: '/app/reviews',
  },
  '/dashboard/pricing-panel': {
    moduleKey: 'marketing_pricing',
    v2Route: '/app/pricings',
  },
  '/dashboard/bank-feeds': {
    moduleKey: 'accounting_bank_feeds',
    v2Route: '/app/finance/bank-transactions',
  },
  '/dashboard/ai-config': {
    moduleKey: 'configuration_ai',
    v2Route: '/app/ai-config',
  },
  '/dashboard/management': {
    moduleKey: 'configuration_integrations',
    v2Route: '/app/integrations-v2',
  },
  '/dashboard/feeds': {
    moduleKey: 'feeds',
    v2Route: '/app/feeds',
    forceV2: true,
  },
  '/dashboard/guest-book': {
    moduleKey: 'guests_guest_book',
    v2Route: '/app/guest-book',
  },
  '/dashboard/invoices': {
    moduleKey: 'accounting_invoices',
    v2Route: '/app/dashboard/invoices',
  },
  '/dashboard/portfolio': {
    moduleKey: 'owners_portfolio',
    v2Route: '/app/dashboard/portfolio/overview',
  },
}

const routes = [
  {
    path: '/app',
    redirect: () => {
      window.location.href = `${getV2BaseUrl()}/app/`
    },
  },
  {
    path: '/storybook',
    component: () => import('components/storybook.vue'),
  },
  { path: '/', redirect: '/dashboard' },
  {
    path: '/onboarding',
    component: PublicLayout,
    children: [
      {
        path: '',
        redirect: 'import',
      },
      {
        path: 'register',
        name: 'onboarding.register',
        component: () => import('components/onboarding/register'),
      },
      {
        path: ':step?',
        name: 'onboarding',
        component: () => import('components/onboarding/OnboardingFlow'),
      },
    ],
  },
  {
    path: '/dashboard',
    component: RootLayout,
    children: [
      {
        name: 'SystemDashboard',
        path: 'system',
        component: () => import('components/system-dashboard'),
      },
      {
        name: 'Taskim',
        path: 'taskim/:boardId?',
        component: () => import('components/taskim/taskim'),
      },
      {
        name: 'Feed',
        path: 'feeds/:feedId?',
        component: () => import('components/feed.vue'),
      },
      {
        name: 'GuestBook',
        path: 'guest-book',
        component: () => import('components/guests/guest-book.vue'),
      },
      {
        name: 'ChannelManager',
        path: 'cm/:paramsTab?',
        component: () => import('components/channel-management/cm-dash.vue'),
      },
      {
        name: 'Project',
        path: 'projects/:projectId/:paramsTab?',
        component: () => import('components/projects/project'),
      },
      {
        name: 'Expense',
        path: 'expenses/:expenseId',
        component: () => import('components/expenses/expense'),
      },
      {
        name: 'Automate',
        path: 'automate/:paramsTab?',
        component: () => import('components/automations-dash'),
      },
      {
        name: 'Estimates',
        path: 'estimates/:paramsTab?/:estimateId?',
        component: () => import('components/estimates/estimates-page'),
      },
      {
        name: 'Listings',
        path: 'listings',
        component: () => import('components/dashboard-page'),
      },
      {
        path: 'sales',
        component: () => import('components/sales-panel'),
      },
      {
        path: 'listings-search',
        component: () => import('components/listings-search'),
      },
      {
        path: 'sales-chat/:id?',
        component: () => import('components/base-chat'),
        name: 'guests-chat',
      },
      {
        path: 'investment-finder',
        component: () => import('components/investment-finder'),
      },
      {
        path: 'listings-setup/:paramsTab?',
        component: () => import('components/listing-setups'),
      },
      {
        name: 'manage-property',
        path: 'edit/:id/:paramsTab?',
        component: () => import('components/listing_management'),
      },
      {
        path: 'experience-management',
        component: () => import('components/experience-manager'),
      },
      {
        path: 'pricing/:id',
        component: () => import('components/pricing-page'),
      },
      {
        name: 'view-property',
        path: 'property/:id/:paramsTab?',
        component: () => import('components/listing/listing-page'),
      },
      {
        path: 'company-activity',
        component: () => import('components/activity'),
      },
      {
        path: 'catalog',
        component: () =>
          import(
            'components/listing-tasks-catalog/listing-task-catalog-items-table'
          ),
      },
      {
        path: 'marketing/:paramsTab?',
        component: () => import('components/marketing'),
      },
      {
        path: 'guests/:id',
        component: () => import('components/guest'),
      },
      {
        path: 'ai-config/:paramsTab?',
        component: () => import('components/ai/ai-manager.vue'),
      },
      {
        path: 'ai-memories',
        component: () => import('components/ai/ai-memories'),
      },
      {
        path: 'processes',
        component: () =>
          import('components/listing-tasks/listing-task-templates.vue'),
      },
      {
        path: 'management/:paramsTab?',
        component: () => import('components/management-page'),
        name: 'setting-page',
        children: [
          {
            name: 'integration-layout',
            path: '/',
            component: () => import('components/integrations-layout.vue'),
            children: [
              {
                path: '/',
                component: () => import('components/integrations.vue'),
              },
              {
                path: 'integration/:name',
                name: 'integration-page',
                component: () =>
                  import('components/marketplace/integration-page.vue'),
              },
            ],
          },
        ],
      },
      {
        path: 'inventory/:paramsTab?',
        component: () => import('components/inventory/inventory-page'),
      },
      {
        path: '/dashboard/listing-setup/:id/:paramsTab?',
        component: () => import('components/listing-setup-page'),
      },
      {
        name: 'payables',
        path: 'payables/:paramsTab?',
        component: () => import('components/payables'),
      },
      {
        name: 'bank-feeds',
        path: 'bank-feeds/:paramsTab?',
        component: () => import('components/bank-feeds'),
      },
      {
        name: 'ledger-wallet',
        path: 'ledger-wallet/:paramsTab?',
        component: () => import('components/accounting/ledger-wallet.vue'),
      },
      {
        name: 'financials',
        path: 'financials/:paramsTab?',
        component: () => import('components/financials'),
      },
      {
        path: 'pricing-panel/:paramsTab?',
        component: () => import('components/pricings'),
      },
      {
        name: 'service-calls',
        path: 'service-calls/:paramsTab?',
        component: () => import('components/listing-tasks-dash'),
        children: [
          {
            name: 'area-report',
            path: '/',
            component: () =>
              import('components/listing-report/listing-report-layout'),
            children: [
              {
                path: '/',
                name: 'region-report',
                component: () =>
                  import('components/listing-report/region-report-page'),
              },
              {
                path: 'listing/:id',
                name: 'listing-report',
                component: () =>
                  import('components/listing-report/listing-report-single'),
              },
            ],
          },
        ],
      },
      {
        path: 'performance/:paramsTab?',
        component: () => import('components/performance'),
      },
      {
        path: 'dev-tools/:paramsTab?',
        component: () => import('components/dev-tools'),
      },
      {
        path: 'listing-task/:id',
        component: () =>
          import(
            'components/listing-tasks/single-listing-task/listing-task-layout'
          ),
        name: 'lt-page',
      },
      {
        path: 'reservations-manage',
        component: () => import('components/reservation-manager'),
      },
      {
        path: 'reservation/:id',
        name: 'reservation-page',
        component: () => import('components/reservation/reservation-page'),
      },
      {
        path: 'field-management',
        component: () => import('components/inventory/requests/requests-table'),
      },
      { path: 'error', component: Error },
      {
        path: 'user-profile/:paramsTab?',
        component: () => import('components/user/user-layout'),
      },
      {
        path: 'operations-settings',
        name: 'operations-settings',
        component: () => import('components/operations/operations-settings'),
      },
      {
        path: 'iot/livion-all-keys',
        name: 'livion-all-keys',
        component: () => import('components/livion/livion-all-keys.vue'),
      },
      {
        path: 'iot/livion-devices/:deviceId/keys',
        name: 'livion-keys',
        component: () => import('components/livion/livion-keys.vue'),
      },
      {
        path: 'iot/:paramsTab?',
        name: 'iot-dashboard',
        component: () => import('components/iot/iot-dashboard'),
      },
      {
        path: 'damage-protection/:tab?',
        name: 'damage-protection',
        component: () =>
          import('components/damage-protection/damage-protection-dashboard'),
      },
      {
        path: 'damage-protection/claim/:claimId',
        name: 'claim-details',
        component: () =>
          import('components/damage-protection/claim-details-page'),
      },
      {
        path: 'store/:paramsTab?',
        component: () => import('components/store/store-view'),
      },
      {
        path: 'hotel-dashboard',
        component: () =>
          import('components/hotel-dashboard/hotel-dashboard-page'),
      },
      {
        path: 'multi-calendar',
        name: 'multi-calendar',
        component: () => import('components/calendar/multi-cal.vue'),
      },
      {
        path: 'multi-calendar_v1',
        name: 'multi-calendar_v1',
        component: () => import('components/calendar/multi-calendar'),
      },
      {
        path: 'multi-calendar_v2',
        name: 'multi-calendar_v2',
        component: () => import('components/calendar/multi-cal.vue'),
      },
      {
        path: 'multi-calendar_v3',
        name: 'multi-calendar_v3',
        component: () => import('components/calendar/multi-cal-v3.vue'),
      },
      {
        path: 'portfolio/:paramsTab?',
        name: 'portfolio',
        component: () => import('components/portfolio/portfolio-page.vue'),
      },
      {
        path: 'guest-experience/:paramsTab?/:conversationId?',
        name: 'sales',
        component: () => import('components/crm/guest-experience'),
      },
      {
        path: 'guest-experience/:paramsTab?',
        name: 'crm',
        component: () => import('components/crm/guest-experience'),
      },
      {
        path: 'reviews-dashboard/:paramsTab?',
        name: 'review-crm',
        component: () => import('components/crm/review-crm.vue'),
      },
      {
        path: 'guest-mgmt/:paramsTab?',
        name: 'guest-exp',
        component: () => import('components/guests/guests-mgmt'),
      },
      {
        path: 'auto-tasks',
        name: 'auto-tasks',
        component: () => import('components/auto-tasks'),
      },
      {
        path: 'auto-messages',
        name: 'auto-messages',
        component: () => import('components/auto-messages'),
      },
      {
        path: 'agent-reservations',
        name: 'agent-reservations',
        component: () => import('components/agents/agent-reservations.vue'),
      },
      { path: '/', redirect: '/dashboard/listings' },
      {
        path: 'management/ledger-accounts/:id',
        name: 'ledger-transactions',
        component: () =>
          import('./components/accounting/ledger-transactions.vue'),
      },
      {
        path: 'multi-cal',
        name: 'dashboard-multi-cal',
        component: () => import('components/calendar/multi-cal.vue'),
      },
      {
        name: 'invoices',
        path: 'invoices/:paramsTab?',
        component: () => import('components/invoices/invoice-page'),
      },
      {
        name: 'websites',
        path: 'websites',
        component: () =>
          import('components/booking-engine/booking-engine-table.vue'),
      },
    ],
  },
  {
    path: '/tourist-lt',
    component: () => import('components/layouts/listing-task-tracker-layout'),
    children: [
      {
        path: '/tourist-lt/:id',
        component: () =>
          import('components/listing-tasks/listing-task-tracker'),
      },
    ],
  },
  {
    path: '/task-report/:id',
    component: () => import('components/listing-tasks/listing-task-report'),
  },
  {
    path: '/management/tenant',
    component: () => import('components/create-tenant-page'),
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordPage,
  },
  {
    path: '*',
    name: 'error',
    component: Error,
  },
]
let pendingNavigation = null

export const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior({ matched: matched1 }, { matched: matched2 }) {
    if (last(matched1).path !== last(matched2).path) {
      return { x: 0, y: 0 }
    }
  },
})
router.beforeEach((to, from, next) => {
  pendingNavigation = to

  // Check if the destination route has a v2 module redirect
  const routePath = to.path
  let mapping = V2_ROUTE_MAPPINGS[routePath]

  // Check for dynamic route matches (prefix-based)
  if (!mapping) {
    for (const [prefix, config] of Object.entries(V2_ROUTE_MAPPINGS)) {
      if (config.isDynamic && routePath.startsWith(prefix)) {
        mapping = config
        break
      }
    }
  }

  if (mapping) {
    let v2Route = mapping.v2Route
    // For dynamic routes, append the remaining path segment (e.g., the :id)
    if (mapping.isDynamic) {
      const prefix = Object.keys(V2_ROUTE_MAPPINGS).find(
        k => V2_ROUTE_MAPPINGS[k] === mapping
      )
      const dynamicPart = routePath.slice(prefix.length)
      v2Route = mapping.v2Route + dynamicPart
    }
    if (mapping.forceV2) {
      store.commit('updateLoading', true)
      window.location.replace(`${getV2BaseUrl()}${v2Route}`)
      return
    }
    const v2Url = getV2RedirectUrl(mapping.moduleKey, v2Route)
    if (v2Url) {
      store.commit('updateLoading', true)
      window.location.replace(v2Url)
      return
    }
  }

  next()
})
router.afterEach((_to, _from, _next) => {
  pendingNavigation = null
})

router.onError(error => {
  console.log(error)
  if (error.message && error.message.includes('Loading chunk')) {
    // Get the destination route that failed
    const destination = pendingNavigation

    if (destination) {
      window.location.href = destination.fullPath
    }
    // Reload the page
  }
})

/*
  route.js

  2026-03-15: Deprecated v1 feeds - forceV2 flag redirects all users to React feeds
  2025-12-24: Improved v2 redirect UX - added loading state and use replace() to avoid history pollution
  2025-12-24: Added v2 module redirect logic to beforeEach navigation guard for React app routing
  2025-05-29: Cleaned up redundant onboarding routes and implemented step-based URL parameters for better navigation
  2024-05-24: Added /dashboard/websites route for website builder (booking-engine-table.vue)
*/
